import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductListSectionRefs
 * @property {HTMLElement} carousel
 */

/**
 * @extends {Component<ProductListSectionRefs>}
 */
class ProductListSection extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#handleKeydown);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const tab = event.target;
    if (!(tab instanceof HTMLButtonElement) || tab.getAttribute('role') !== 'tab') return;

    let direction = 0;
    if (event.key === 'ArrowRight') direction = 1;
    if (event.key === 'ArrowLeft') direction = -1;
    if (!direction) return;

    const tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    const currentIndex = tabs.indexOf(tab);
    if (currentIndex === -1) return;

    event.preventDefault();
    const nextIndex = (currentIndex + direction + tabs.length) % tabs.length;
    const nextTab = tabs[nextIndex];
    nextTab.focus();
    this.#activateCollection(nextTab.dataset.collectionHandle);
  };

  /**
   * @param {MouseEvent | KeyboardEvent} event
   */
  selectCollection(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-collection-handle]');
    if (!(button instanceof HTMLButtonElement)) return;

    this.#activateCollection(button.dataset.collectionHandle);
  }

  /**
   * @param {string | undefined} collectionHandle
   */
  #activateCollection(collectionHandle) {
    if (!collectionHandle || collectionHandle === this.dataset.activeCollection) return;

    const panels = this.querySelectorAll('[data-collection-panel]');
    const tabs = this.querySelectorAll('[role="tab"]');

    for (const panel of panels) {
      if (!(panel instanceof HTMLElement)) continue;
      const isActive = panel.dataset.collectionPanel === collectionHandle;
      panel.toggleAttribute('hidden', !isActive);
      panel.classList.toggle('product-list-panel--active', isActive);
    }

    for (const tab of tabs) {
      if (!(tab instanceof HTMLButtonElement)) continue;
      const isSelected = tab.dataset.collectionHandle === collectionHandle;
      tab.setAttribute('aria-selected', String(isSelected));
      tab.classList.toggle('product-list-collection-tabs__tab--active', isSelected);
      tab.tabIndex = isSelected ? 0 : -1;
    }

    this.dataset.activeCollection = collectionHandle;
  }
}

if (!customElements.get('product-list-section')) {
  customElements.define('product-list-section', ProductListSection);
}
